import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { OnlineSalesUnauthenticated } from 'gw-edge-client';

import {
    type AddCoverageDTO,
    type RemoveCoveragePriceDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import { useEffect, useState } from 'react';
import { opcInfoAtom } from '../atoms/opcInfoAtom';
import { type AddRemoveCoverageIdentifier } from '../types/opcTypes';
import { jobInfoAtom } from '../atoms/jobInfoAtom';
import { isOpcFetchingAtom } from '../atoms/isOpcFetchingAtom';
import { jobAtom } from '../atoms/jobAtom';
import { DRAFT } from '../types/types';
import { isLoggedOnSelector } from '../selectors/isLoggedOnSelector';

const useRemoveCoverageOPC = (): {
    removeCoveragePriceDTO: RemoveCoveragePriceDTO | undefined;
    revertChange: () => Promise<void>;
} => {
    const { jobNumber, sessionId } = useRecoilValue(jobInfoAtom);
    const isLoggedOn = useRecoilValue(isLoggedOnSelector);
    const setIsOpcFetching = useSetRecoilState(isOpcFetchingAtom);
    const [removeCoveragePriceDTO, setRemoveCoveragePriceDTO] = useState<RemoveCoveragePriceDTO>();
    const opcInfo = useRecoilValue(opcInfoAtom);
    const [jobData, setJobData] = useRecoilState(jobAtom);
    const { productName, coverableId, coverageId } = opcInfo;
    const addRemoveCoverage: AddRemoveCoverageIdentifier = {
        productName,
        coverableId,
        coverageId: coverageId ?? ''
    };

    if (!jobNumber) {
        throw new Error('Invalid jobId');
    }

    if (!sessionId) {
        throw new Error('Invalid sessionId');
    }

    useEffect(() => {
        const removeCoverageOPC = async (): Promise<void> => {
            setIsOpcFetching(true);
            await OnlineSalesUnauthenticated.calculateRemoveCoveragePriceForOPC(
                jobNumber,
                sessionId,
                addRemoveCoverage,
                !isLoggedOn
            )
                .then((res) => {
                    setRemoveCoveragePriceDTO(res);
                    setJobData({ ...jobData, jobStatus: DRAFT });
                })
                .catch(() => {
                    return undefined;
                })
                .finally(() => {
                    setIsOpcFetching(false);
                });
        };
        void removeCoverageOPC();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addRemoveCoverage.coverageId]);

    const revertChange = async (): Promise<void> => {
        setIsOpcFetching(true);
        const addCoverageDTO: AddCoverageDTO = {
            ...addRemoveCoverage,
            isUpdateOnly: false
        };
        await OnlineSalesUnauthenticated.calculateAddCoveragePriceForOPC(
            jobNumber,
            sessionId,
            addCoverageDTO,
            !isLoggedOn
        )
            .catch(() => {
                return undefined;
            })
            .finally(() => {
                setIsOpcFetching(false);
            });
    };

    return { removeCoveragePriceDTO, revertChange };
};

export default useRemoveCoverageOPC;
